'use client';

import {
	CloseButton, Dialog, For, Icon, Portal,
	SegmentGroup, Text, Button,
	Center,
	Spinner,
	Stack,
} from '@chakra-ui/react';
import type {
	DialogBodyProps,
	DialogFooterProps,
	DialogTitleProps,
	DialogRootProps,
	DialogTriggerProps,
	SegmentGroupRootProps,
	StackProps,
} from '@chakra-ui/react';
import type React from 'react';
import type { PropsWithChildren } from 'react';

import { NestedCheckbox  } from '../NestedCheckbox/NestedCheckbox';
import type { NestedCategory } from '../NestedCheckbox/NestedCheckbox';

type CategorySelectCheckListProps = {
	collections: NestedCategory[]
	isLoading?:  boolean
	selectedIds: string[]

	onSelect: (id: string, checked: boolean) => void
} & Omit<StackProps, 'onSelect'>;

export const CategorySelectCheckList = ({
	collections,
	isLoading,
	onSelect,
	selectedIds,
	...props
}: CategorySelectCheckListProps) => {
	if (isLoading) return (
		<Center h="50vh" p={4} w="100%">
			<Spinner />
		</Center>
	);

	return (
		<Stack
			maxH="70vh"
			overflowY="auto"
			p={4}
			w="100%"
			{...props}
		>
			{collections.map(node => (
				<NestedCheckbox
					key={node.id}
					node={node}
					onSelect={onSelect}
					selectedIds={selectedIds}
				/>
			))}
		</Stack>
	);
};

type SelectorCategoryListProps = {
	groups:        { id: string

		label: string
		value: string }[]

	onSelectGroup: (categoryId: null | string) => void
} & SegmentGroupRootProps;

const CategorySelectSidebar = ({ groups, onSelectGroup, ...props }: SelectorCategoryListProps) => {
	return (
		<SegmentGroup.Root
			bg="primary.10"
			onValueChange={e => onSelectGroup(e.value)}
			orientation="vertical"
			{...props}
		>
			<SegmentGroup.Indicator />
			<For each={groups}>
				{group => (
					<SegmentGroup.Item
						h="fit-content"
						key={group.id}
						maxLines={2}
						textOverflow="ellipsis"
						textWrap="wrap"
						value={group.value}
						whiteSpace="break-spaces"
					>
						<SegmentGroup.ItemText
							p={4}
							textAlign="start"
							textTransform="capitalize"
							w="250px"
						>
							{group.label}
							<SegmentGroup.ItemHiddenInput />
						</SegmentGroup.ItemText>
					</SegmentGroup.Item>
				)}
			</For>
		</SegmentGroup.Root>
	);
};

type CategorySelectSubmitButtonProps = {
	count: number
	label: string
};

const CategorySelectActionButton = ({ count, label }: CategorySelectSubmitButtonProps) => {
	return (
		<>
			<Text>
				{`${count}: Results`}
			</Text>
			<Dialog.ActionTrigger>
				<Button>
					{label}
				</Button>
			</Dialog.ActionTrigger>
		</>
	);
};

const CategorySelectFooter = ({ children, ...dialogFooterProps }: PropsWithChildren<DialogFooterProps>) => {
	return (
		<Dialog.Footer
			display="flex"
			gap={6}
			placeContent="center"
			pt={6}
			{...dialogFooterProps}
		>
			{children}
		</Dialog.Footer>
	);
};

const CategorySelectContent = ({ children }: PropsWithChildren) => {
	return (
		<Portal>
			<Dialog.Backdrop />
			<Dialog.Positioner>
				<Dialog.Content>
					{children}
				</Dialog.Content>
			</Dialog.Positioner>
		</Portal>
	);
};

type CategorySelectHeaderProps = {
	icon:  React.ElementType
	title: string
} & DialogTitleProps;

const CategorySelectHeader = ({ icon, title, ...dialogTitleProps }: CategorySelectHeaderProps) => {
	return (
		<Dialog.Header py={6}>
			<Dialog.Title
				alignItems="center"
				display="flex"
				gap={2}
				textStyle="h3"
				{...dialogTitleProps}
			>
				<Icon as={icon} color="primary.100" />
				{title}
			</Dialog.Title>
		</Dialog.Header>
	);
};

const CategorySelectBody = ({ children, ...dialogBodyProps }: PropsWithChildren<DialogBodyProps>) => {
	return (
		<Dialog.Body
			border="1px solid"
			borderColor="primary.100"
			borderRadius="lg"
			mx={6}
			overflow="hidden"
			p={0}
			{...dialogBodyProps}
		>
			{children}
		</Dialog.Body>
	);
};

const CategorySelectTrigger = ({ children, ...dialogTriggerProps }: PropsWithChildren<DialogTriggerProps>) => {
	return (
		<Dialog.Trigger asChild {...dialogTriggerProps}>
			{children}
		</Dialog.Trigger>
	);
};

const CategorySelectorRoot = ({ children, ...dialogRootProps }: PropsWithChildren<DialogRootProps>) => {
	return (
		<Dialog.Root {...dialogRootProps} size="xl">
			{children}
		</Dialog.Root>
	);
};

const CategorySelectorCloseButton = () => {
	return                   (
		<Dialog.CloseTrigger asChild>
			<CloseButton size="md" variant="plain" />
		</Dialog.CloseTrigger>
	);
};

export const CategorySelectDialog = {
	ActionButton: CategorySelectActionButton,
	Body:         CategorySelectBody,
	Checklist:    CategorySelectCheckList,
	CloseIcon:    CategorySelectorCloseButton,
	Content:      CategorySelectContent,
	Footer:       CategorySelectFooter,
	Header:       CategorySelectHeader,
	Root:         CategorySelectorRoot,
	SideBar:      CategorySelectSidebar,
	Trigger:      CategorySelectTrigger,
};
